<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
    <div
      class="mx-auto w-full max-w-[960px] shrink-0 px-4 pt-[calc(env(safe-area-inset-top)+62px)] tablet-h:pt-8"
    >
      <h1 class="mb-[18px] text-[20px] leading-[26px] text-gray-800">
        <template v-if="total !== undefined">
          {{ formatSearchTotal(total) }}
          {{ total === 1 ? 'registration' : 'registrations' }} for "{{ query }}"
        </template>
        <template v-else>Searching…</template>
      </h1>

      <div class="mb-4 flex items-center gap-2.5">
        <SearchFilterMenu v-model="filterBy" />
        <SearchSortMenu v-model="sortBy" />
      </div>

      <div v-if="loading || files.length" class="border-t border-[#ebedef]" />
    </div>

    <div
      ref="scroller"
      class="min-h-0 flex-1 overflow-y-auto"
      @scroll="handleScroll"
    >
      <div
        class="mx-auto w-full max-w-[960px] px-4 pb-[calc(env(safe-area-inset-bottom)+40px)] tablet-h:pb-10"
      >
        <template v-if="loading">
          <div
            v-for="n in 6"
            :key="n"
            class="flex gap-5 border-b border-[#ebedef] px-[10px] py-[18px]"
          >
            <Skeleton class="size-[68px] shrink-0 rounded-md" />
            <div class="flex-1">
              <Skeleton class="mb-3 h-[15px] w-[220px]" />
              <Skeleton class="mb-2 h-3 w-[160px]" />
              <Skeleton class="h-3 w-[180px]" />
            </div>
          </div>
        </template>
        <template v-else-if="files.length">
          <PublicSearchRow v-for="file in files" :key="file.id" :file="file" />
        </template>
        <div
          v-else
          class="flex flex-col items-center justify-center px-6 pt-24 text-center"
        >
          <div class="text-[16px] font-medium leading-[20px] text-gray-800">
            No results found
          </div>
          <div class="mt-1.5 text-[14px] leading-[20px] text-gray-500">
            Try different keywords
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { Skeleton } from '@dacr/library'
import { formatSearchTotal } from '@/features/search/formatSearchTotal'
import {
  FileCategory,
  SearchSort,
  useSearchFilesQuery,
  type SearchFile,
} from '@/shared/graphql/legacy/generated'
import SearchFilterMenu from '@/features/search/components/desktop/SearchFilterMenu.vue'
import SearchSortMenu from '@/features/search/components/desktop/SearchSortMenu.vue'
import PublicSearchRow from '@/features/search/components/desktop/PublicSearchRow.vue'

const route = useRoute()

const query = computed(() => (route.query.query as string) ?? '')

useHead({
  title: computed(
    () => `Search results in Dacr public files for "${query.value}" | Dacr`
  ),
})

const scroller = ref<HTMLElement>()

const filterBy = ref<FileCategory[]>([])
const sortBy = ref<SearchSort>(SearchSort.TimestampDesc)

const variables = computed(() => ({
  query: query.value,
  filterBy: filterBy.value,
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useSearchFilesQuery(variables)

const total = computed(() => result.value?.searchFiles.total)
const files = computed<SearchFile[]>(
  () => (result.value?.searchFiles.files as SearchFile[] | undefined) ?? []
)

const fetchNextPage = () => {
  const next = result.value?.searchFiles.next
  if (!next || loading.value) return
  fetchMore({
    variables: { next },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData
      return {
        __typename: 'Query' as const,
        searchFiles: {
          __typename: 'SearchFilesResponse' as const,
          files: [
            ...previousData.searchFiles.files,
            ...newData.searchFiles.files,
          ],
          next: newData.searchFiles.next,
          total: newData.searchFiles.total,
        },
      }
    },
  })
}

const handleScroll = () => {
  const el = scroller.value
  if (!el) return
  if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= 0.8) {
    fetchNextPage()
  }
}
</script>
